<template>
  <div class="h-screen overflow-hidden flex flex-col justify-between bg-white border-solid border-l border-0 border-gray-border">
    <div class="flex-1 min-h-0 overflow-y-auto">
      <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
        <h3 class="font-semibold mt-8 truncate">
          <span class="text-gray-dark">{{ botDisplayName }}</span>
          <span class="text-gray ml-1">/</span>
          <span class="text-black ml-1">Add channel</span>
        </h3>
      </div>

      <base-tabs :tabs="tabs" :value="tab" class="px-8 mt-6" @input="tab = $event"/>

      <template v-if="tab === TABS.CREATE">
        <template v-if="!isSelectingDaktelaParameters">
          <div class="px-8 mt-6">
            <base-label id="newChannelName" label="Name"/>
            <base-input id="newChannelName"
                        name="newChannelName"
                        variant="editor"
                        type="string"
                        class="w-full border-gray py-px mt-1 h-10"
                        v-model.trim="channelName"
            />
          </div>

          <div class="px-8 mt-6">
            <base-label id="newChannelType" label="Type"/>
            <queryable-select id="newChannelType"
                              :value="channelType"
                              :options="channelTypeOptions"
                              class="w-full mt-1 h-10"
                              @input="channelType = $event"
            />
          </div>
        </template>

        <daktela-email-channel-form v-if="isDaktelaEmail"
                                    ref="daktelaForm"
                                    :existing-channels="existingChannels"
                                    class="px-8 mt-8"
                                    @change="daktelaState = $event"
        />

        <template v-else>
          <div class="px-8 mt-8 pt-6 border-0 border-t border-solid border-gray-border font-semibold text-black">
            CHAT SETTINGS
          </div>

          <SettingsBotsForm :selected-theme="selectedTheme"
                            :selected-lang-config="selectedLangConfig"
                            :is-text-override-enabled="isTextOverrideEnabled"
                            :themes="themes"
                            @update:selected-theme="updateSelectedTheme"
                            @update:selected-lang-config="updateSelectedLangConfig"
                            @update:is-text-override-enabled="updateIsTextOverrideEnabled"
                            class="px-8"
          />
        </template>
      </template>

      <div v-else class="px-8 mt-6">
        <p id="existingChannelsLabel" class="text-gray-dark">Existing channels</p>
        <p v-if="!existingChannels.length" class="text-gray-dark mt-2">
          There are no existing {{ bot.type }} channels in this organization. Create a new one instead.
        </p>
        <div v-else
             role="group"
             aria-labelledby="existingChannelsLabel"
             class="mt-1 border border-solid border-gray-border rounded-lg overflow-hidden"
        >
          <button v-for="(channel, i) in existingChannels"
                  :key="channel.id"
                  type="button"
                  class="w-full px-4 py-3 flex items-center justify-between text-left bg-transparent border-0 border-solid border-soft"
                  :class="{
                    'border-b': i < existingChannels.length - 1,
                    'cursor-pointer hover:bg-soft': channel.isDeployable,
                    'cursor-not-allowed opacity-60': !channel.isDeployable,
                    'bg-primary-light': channel.id === selectedChannelId
                  }"
                  :disabled="!channel.isDeployable"
                  :aria-pressed="channel.id === selectedChannelId"
                  @click="selectedChannelId = channel.id"
          >
            <span class="flex items-center gap-2 text-black min-w-0">
              <ChannelTypeIcons :selected-channels="[channel]" :has-tooltip="false"/>
              <span class="truncate">{{ channel.name }}</span>
            </span>
            <span class="text-sm ml-3 flex-shrink-0" :class="channel.isDeployable ? 'text-gray-dark' : 'text-gray'">
              {{ channel.isDeployable ? 'Available' : `Deployed to ${channel.boundBotLabel}` }}
            </span>
          </button>
        </div>
        <p v-if="!isDaktelaEmail" class="text-gray-dark text-sm mt-4">
          Deploying an Amio Chat channel applies this market's chat theme to it and replaces the channel's current widget appearance.
        </p>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-4 mb-6 px-8 pt-4 flex-shrink-0 bg-white">
      <busy-button id="addChannelSaveButton"
                   variant="primary"
                   class="col-span-1"
                   :is-loading="isSubmitting"
                   :is-disabled="isSaveDisabled || isSubmitting"
                   @click="submit"
      >
        {{ submitLabel }}
      </busy-button>
      <base-button variant="bordered"
                   class="col-span-1 justify-center"
                   data-testid="add-channel-secondary"
                   @click="secondaryAction"
      >
        {{ secondaryLabel }}
      </base-button>
    </div>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import BaseButton from '@/components/base-components/BaseButton.vue'
import BaseInput from '@/components/base-components/BaseInput.vue'
import BaseTabs from '@/components/base-components/BaseTabs.vue'
import ChannelTypeIcons from '@/components/analytics/filter/ChannelTypeIcons.vue'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import SettingsBotsForm from '@/components/settings/bots/SettingsBotsForm.vue'
import DaktelaEmailChannelForm from '@/components/settings/bots/DaktelaEmailChannelForm.vue'
import chatThemeService from '@/services/amio/chat-theme.service'
import equals from 'ramda/src/equals'
import isEmpty from 'ramda/src/isEmpty'
import isNil from 'ramda/src/isNil'
import type from 'ramda/src/type'
import filter from 'ramda/src/filter'
import map from 'ramda/src/map'
import pipe from 'ramda/src/pipe'
import reject from 'ramda/src/reject'
import anyPass from 'ramda/src/anyPass'
import { DEFAULT_LANG_CONFIG } from '../../../constants/chat-theme.constants'
import { marketNameForBot } from '@/utils/market.utils'
import { BOT_TYPES } from '@/utils/bot.constants'
import { CHANNEL_TYPES } from '@/utils/channel.constants'

const TABS = Object.freeze({
  CREATE: 'create',
  DEPLOY: 'deploy'
})

export default {
  name: 'SettingsBotAddChannelEditor',

  components: {
    BusyButton,
    BaseButton,
    BaseInput,
    BaseTabs,
    ChannelTypeIcons,
    QueryableSelect,
    SettingsBotsForm,
    DaktelaEmailChannelForm
  },

  props: {
    bot: {
      type: Object,
      required: true
    },
    // org channels this bot could be deployed to - see buildDeployableChannels (channel.utils.js);
    // each has isDeployable and, when bound elsewhere, boundBotLabel
    existingChannels: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  emits: ['cancel', 'create', 'deploy'],

  data() {
    const isEmailBot = this.bot?.type === BOT_TYPES.EMAIL
    return {
      TABS,
      tab: TABS.CREATE,
      selectedChannelId: null,
      channelName: '',
      channelType: isEmailBot ? CHANNEL_TYPES.EMAIL_DAKTELA : CHANNEL_TYPES.AMIO_CHAT,
      daktelaState: {
        step: 1,
        canSubmit: false,
        isLoadingDetails: false,
        payload: {}
      },
      themes: [],
      selectedTheme: {
        themeConfig: {},
        langConfig: {}
      },
      originalTheme: {},
      isTextOverrideEnabled: false
    }
  },

  computed: {
    // For a market variant, shows the real market name (not the variant's own drift-prone
    // name/multi_bot_name); for a standalone bot (no market), marketNameForBot degrades to bot.name.
    botDisplayName() {
      return marketNameForBot(this.bot)
    },
    tabs() {
      return [
        { key: TABS.CREATE, label: 'Create' },
        { key: TABS.DEPLOY, label: 'Deploy' }
      ]
    },
    isDaktelaEmail() {
      return this.channelType === CHANNEL_TYPES.EMAIL_DAKTELA
    },
    isSubmitting() {
      return this.isLoading || this.daktelaState.isLoadingDetails
    },
    submitLabel() {
      if (this.tab === TABS.DEPLOY) return 'Deploy'
      if (this.isDaktelaEmail && this.daktelaState.step !== 3) return 'Next'
      return 'Create'
    },
    isSelectingDaktelaParameters() {
      return this.isDaktelaEmail && this.tab === TABS.CREATE && this.daktelaState.step === 3
    },
    secondaryLabel() {
      return this.isSelectingDaktelaParameters ? 'Back' : 'Cancel'
    },
    channelTypeOptions() {
      return [
        { text: 'Amio Chat', value: CHANNEL_TYPES.AMIO_CHAT },
        { text: 'Daktela Email', value: CHANNEL_TYPES.EMAIL_DAKTELA }
      ]
    },
    selectedLangConfig() {
      return this.selectedTheme.langConfig?.default || DEFAULT_LANG_CONFIG.default
    },
    selectedLanguage() {
      return this.bot.language || 'en'
    },
    isSaveDisabled() {
      if (this.tab === TABS.DEPLOY) return !this.selectedChannelId
      if (!this.channelName || !this.channelType) return true
      if (this.isDaktelaEmail) return !this.daktelaState.canSubmit
      return false
    }
  },

  watch: {
    channelType(type) {
      if (type === CHANNEL_TYPES.AMIO_CHAT && !this.themes.length) this.initThemes()
    }
  },

  methods: {
    cleanObject(obj) {
      if (isNil(obj) || type(obj) !== 'Object') return obj

      const isFalsy = anyPass([isNil, equals(''), isEmpty])
      const cleanedEntries = pipe(
        Object.entries,
        reject(([key, value]) => isFalsy(value)),
        map(([key, value]) => {
          if (type(value) === 'Object') {
            const cleanedValue = this.cleanObject(value)
            return isEmpty(cleanedValue) ? null : [key, cleanedValue]
          }
          return [key, value]
        }),
        filter(entry => entry !== null)
      )(obj)

      return Object.fromEntries(cleanedEntries)
    },
    updateIsTextOverrideEnabled(isTextOverrideEnabled) {
      this.isTextOverrideEnabled = isTextOverrideEnabled
    },
    updateSelectedLangConfig(langConfig) {
      this.selectedTheme = {
        ...this.selectedTheme,
        langConfig: {
          ...this.selectedTheme.langConfig,
          default: langConfig ? this.cleanObject(langConfig) : {}
        }
      }
    },
    updateSelectedTheme(theme) {
      const cleanThemeConfig = theme?.themeConfig ? this.cleanObject(theme.themeConfig) : {}
      const cleanLangConfig = theme?.langConfig ? this.cleanObject(theme.langConfig) : {}
      this.selectedTheme = {
        ...(theme || {}),
        themeConfig: cleanThemeConfig,
        langConfig: cleanLangConfig
      }
    },
    secondaryAction() {
      if (this.isSelectingDaktelaParameters) {
        this.$refs.daktelaForm.back()
        return
      }
      this.$emit('cancel')
    },
    async submit() {
      if (this.tab === TABS.DEPLOY) {
        this.$emit('deploy', this.selectedChannelId)
        return
      }
      if (this.isDaktelaEmail) {
        if (this.daktelaState.step !== 3) {
          await this.$refs.daktelaForm.loadDetails()
          return
        }
        this.$emit('create', {
          name: this.channelName,
          type: CHANNEL_TYPES.EMAIL_DAKTELA,
          ...this.daktelaState.payload
        })
        return
      }
      this.$emit('create', {
        name: this.channelName,
        type: this.channelType,
        selectedTheme: this.selectedTheme,
        originalTheme: this.originalTheme,
        selectedLanguage: this.selectedLanguage,
        isTextOverrideEnabled: this.isTextOverrideEnabled
      })
    },
    async initThemes() {
      this.themes = await chatThemeService.list()
      if (!this.bot.chat_theme_id) {
        this.selectedTheme = this.themes[0] || { themeConfig: {}, langConfig: {} }
      } else {
        this.selectedTheme = await chatThemeService.get(this.bot.chat_theme_id)
      }

      this.selectedTheme = {
        ...this.selectedTheme,
        langConfig: {
          ...(this.selectedTheme.langConfig || {}),
          default: {
            ...(this.selectedTheme.langConfig?.default || {})
          }
        }
      }
      this.originalTheme = JSON.parse(JSON.stringify(this.selectedTheme))
    }
  },

  async mounted() {
    if (this.isDaktelaEmail) return
    await this.initThemes()
  }
}
</script>
